import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { Users, ShieldCheck, Sparkles, DollarSign, Eye, Share2, Rocket, TrendingUp, Building2 } from "lucide-react";

const kpi = (icon, label, value, tone = "emerald") => ({ icon, label, value, tone });

export default function AdminInsights() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/insights").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === null) return <div className="p-10 text-slate-500">Loading insights…</div>;
  if (d === false) return <div className="p-10 text-slate-500">Could not load insights.</div>;

  const rev = (d.totals.revenue_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const kpis = [
    kpi(DollarSign,  "Total revenue",    `$${rev}`),
    kpi(Users,       "Total users",      d.totals.users),
    kpi(Building2,   "Properties",       d.totals.properties),
    kpi(ShieldCheck, "Certified",        d.totals.certified),
    kpi(Sparkles,    "Premium tier",     d.totals.premium),
    kpi(Sparkles,    "Verify™ waitlist", d.totals.waitlist, "gold"),
  ];

  const funnel = [
    { name: "Public views",  value: d.funnel.views,      fill: "#0EA5E9" },
    { name: "Shares",        value: d.funnel.shares,     fill: "#10B981" },
    { name: "Badge dl",      value: d.funnel.downloads,  fill: "#8B5CF6" },
    { name: "Milestones",    value: d.funnel.milestones, fill: "#D4AF37" },
    { name: "Paid upgrades", value: d.funnel.upgrades,   fill: "#0A1128" },
  ];

  const upgradeRate = d.funnel.milestones > 0 ? Math.min(100, Math.round((d.funnel.upgrades / d.funnel.milestones) * 100)) : 0;

  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Admin · Insights</div>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight text-[#0A1128]">Platform funnel.</h1>
        <p className="mt-2 text-slate-600">Every KPI investors ask about — in one screen.</p>

        {/* KPIs */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {kpis.map((k) => (
            <div key={k.label} data-testid={`kpi-${k.label}`}
                 className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${k.tone === "gold" ? "bg-[#F9F3E5] text-[#0A1128]" : "bg-emerald-50 text-emerald-700"}`}>
                <k.icon className="w-4 h-4" />
              </div>
              <div className="mt-3 font-serif text-3xl text-[#0A1128]">{k.value}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mt-1">{k.label}</div>
            </div>
          ))}
        </div>

        {/* Funnel */}
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white rounded-3xl border border-slate-200 p-6">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Conversion funnel</div>
                <h2 className="mt-1 font-serif text-2xl text-[#0A1128]">Certified reach → paid upgrades</h2>
              </div>
              <div className="text-xs text-slate-500">Milestone → Upgrade rate: <b className="text-[#0A1128]">{upgradeRate}%</b></div>
            </div>
            <div className="mt-6 h-64">
              <ResponsiveContainer>
                <BarChart data={funnel} layout="vertical" margin={{ top: 4, right: 16, left: 80, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#0A1128" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {funnel.map((f, i) => <Cell key={i} fill={f.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-[#0A1128] text-white rounded-3xl p-6 flex flex-col justify-between">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">Investor pitch</div>
              <h3 className="mt-2 font-serif text-2xl">The numbers behind the vision.</h3>
              <p className="mt-3 text-slate-300 text-sm">
                Every certified badge drives public views → shares → milestone → upgrade. This funnel is the growth engine.
              </p>
            </div>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-center gap-2"><Eye className="w-4 h-4 text-[#D4AF37]" /> <b>{d.funnel.views}</b> total public views</li>
              <li className="flex items-center gap-2"><Share2 className="w-4 h-4 text-[#D4AF37]" /> <b>{d.funnel.shares}</b> one-click shares</li>
              <li className="flex items-center gap-2"><Rocket className="w-4 h-4 text-[#D4AF37]" /> <b>{d.funnel.milestones}</b> growth milestones hit</li>
              <li className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#D4AF37]" /> <b>{d.funnel.upgrades}</b> premium upgrades paid</li>
            </ul>
          </div>
        </div>

        {/* 14d trend */}
        <div className="mt-6 bg-white rounded-3xl border border-slate-200 p-6">
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Activity — last 14 days</div>
          <div className="mt-4 h-40">
            <ResponsiveContainer>
              <BarChart data={d.series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(v) => v.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} allowDecimals={false} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="events" radius={[6, 6, 0, 0]}>
                  {d.series.map((_, i) => <Cell key={i} fill="#10B981" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
